import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import WaitlistCTA from "@/components/landing/WaitlistCTA";
import { HeartScribble } from "@/components/landing/Logo";
import useReveal from "@/hooks/useReveal";
import useSEO from "@/hooks/useSEO";

const principles = [
    {
        title: "Less choice. Better attention.",
        copy: "The infinite feed makes every person feel replaceable. LÜVYN gives you five considered introductions a week so each one gets a real chance.",
    },
    {
        title: "Compatibility has texture.",
        copy: "Values, conflict style, pace of life, humor, curiosity, privacy, and emotional availability matter more than a good opener.",
    },
    {
        title: "Showing up should count.",
        copy: "The people who reply, clarify, reschedule kindly, and close loops should be easier to meet than the people who disappear.",
    },
    {
        title: "The app should get out of the way.",
        copy: "A dating product is successful when it creates enough trust for two people to leave the product behind.",
    },
];

const letters = [
    "No swipe casino.",
    "No public popularity contests.",
    "No selling your story.",
    "No pretending chemistry is only a prompt.",
];

export default function Manifesto() {
    useReveal();
    useSEO({
        title: "Manifesto",
        description:
            "Dating got loud. We made it quiet. Read our operating principles for less choice and better attention.",
        canonical: "/manifesto",
        schema: {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
                {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Home",
                    "item": "https://luvyn.com/"
                },
                {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Manifesto",
                    "item": "https://luvyn.com/manifesto"
                }
            ]
        }
    });

    return (
        <div className="relative min-h-screen overflow-x-hidden" data-testid="manifesto-page">
            <div className="grain" />
            <Header />
            <main id="top">
                <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 bg-[var(--paper)]">
                    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                        <div className="reveal in flex items-center justify-between flex-wrap gap-3 pb-6 border-b-2 border-[var(--ink)] font-mono text-[10px] uppercase tracking-[0.25em]">
                            <span>Manifesto edition</span>
                            <span className="hidden sm:inline">For people tired of performing</span>
                            <span>Vol. 01</span>
                        </div>

                        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-end mt-12">
                            <div className="reveal in">
                                <div className="stamp stamp-large mb-7">The slow love brief</div>
                                <h1 className="font-display text-[18vw] sm:text-[12vw] lg:text-[10.5rem] leading-[0.82] tracking-tighter">
                                    Dating got{" "}
                                    <span className="font-italic-serif text-[var(--hot)]">
                                        loud.
                                    </span>
                                    <br />
                                    We made it quiet.
                                </h1>
                            </div>
                            <div className="reveal in border-2 border-[var(--ink)] bg-[var(--paper-2)] p-7 lg:p-9">
                                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--hot)] mb-5">
                                    Editor's note
                                </div>
                                <p className="font-display text-3xl lg:text-4xl leading-tight">
                                    LÜVYN is built around restraint: fewer matches,
                                    clearer intent, stronger trust, and a dating rhythm
                                    that leaves room for an actual life.
                                </p>
                                <div className="mt-8 flex items-center gap-3 font-hand text-2xl text-[var(--hot)]">
                                    <HeartScribble className="w-7 h-7" color="currentColor" />
                                    not another swipe machine
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="relative py-24 lg:py-32 bg-[var(--paper-2)] border-y-2 border-[var(--ink)]">
                    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                        <div className="grid lg:grid-cols-[0.75fr_1.25fr] gap-12">
                            <div className="reveal">
                                <div className="font-mono text-[10px] uppercase tracking-[0.3em] mb-4">
                                    Four operating principles
                                </div>
                                <h2 className="font-display text-6xl lg:text-8xl leading-[0.86] tracking-tighter">
                                    The rules we{" "}
                                    <span className="font-italic-serif text-[var(--hot)]">
                                        refuse
                                    </span>{" "}
                                    to break.
                                </h2>
                            </div>
                            <div className="reveal grid sm:grid-cols-2 gap-5">
                                {principles.map((principle, index) => (
                                    <article
                                        key={principle.title}
                                        className="bg-[var(--paper)] border-2 border-[var(--ink)] p-7 min-h-[250px] flex flex-col"
                                    >
                                        <div className="font-display text-5xl text-[var(--hot)]">
                                            {String(index + 1).padStart(2, "0")}
                                        </div>
                                        <h3 className="mt-8 font-display text-3xl leading-tight">
                                            {principle.title}
                                        </h3>
                                        <p className="mt-4 text-sm leading-relaxed text-[var(--ink-soft)]">
                                            {principle.copy}
                                        </p>
                                    </article>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="relative py-24 lg:py-32">
                    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                        <div className="reveal border-y-2 border-[var(--ink)] py-10">
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--hot)] mb-8">
                                House refusals
                            </div>
                            <div className="grid md:grid-cols-2 gap-x-10">
                                {letters.map((line) => (
                                    <div
                                        key={line}
                                        className="font-display text-5xl lg:text-7xl leading-[0.9] border-t border-[var(--ink)] py-7"
                                    >
                                        {line}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                <WaitlistCTA />
            </main>
            <Footer />
        </div>
    );
}
